import C from "../Constants";

import EventHelperCommon from "../scheduler-event-helper/common";
import EventHelperVessel from "../scheduler-event-helper/vessel";

export default async (project, changes) => {
  const eventStore = project.eventStore;
  // 削除、変更されたイベント差分を取得
  const { modified, removed } = changes;

  // イベントが削除された場合
  // 同じ乗船期間IDのイベントは全て削除
  if (removed.length > 0) {
    for (const event of removed) {
      // 計画中は無視
      if (event.scheduling_status == C.SCHEDULING_STATUS.PLANNING) {
        continue;
      }

      await _processVesselEventDeletedAsync(event, eventStore);
    }
  }

  // イベントが更新された場合
  if (modified.length > 0) {
    for (const event of modified) {
      // 計画中は無視
      if (event.scheduling_status == C.SCHEDULING_STATUS.PLANNING) {
        continue;
      }

      await _processEventChangedAsync(event, eventStore);
    }
  }
};

/**
 * 船単位イベント削除された場合の処理
 *
 * 削除されたイベントの前の役職期間を削除イベントの終了日まで伸ばす。
 *
 * 先頭が消された場合は何もしない
 * @param {*} deletedEvent 削除されたイベント
 * @param {*} store
 */
const _processVesselEventDeletedAsync = async (deletedEvent, store) => {
  let targetEvents = store.query(event => {
    return (
      deletedEvent.enterprise_manning_vessel_crew_id ===
      event.enterprise_manning_vessel_crew_id
    );
  });

  if (targetEvents.length === 0) {
    return;
  }

  targetEvents.push(deletedEvent);
  targetEvents.sort(EventHelperCommon.sortByStartDate);

  const deletedEventIndex = targetEvents.findIndex(
    event => event.id === deletedEvent.id
  );

  if (deletedEventIndex <= 0) {
    return;
  }

  const targetEvent = targetEvents[deletedEventIndex - 1];
  await targetEvent.setEndDate(deletedEvent.endDate, false);
};

/**
 * 船単位のイベントが変更された場合の処理
 * @param {*} editedEvent 変更されたイベント
 * @param {*} store イベントストア
 */
const _processEventChangedAsync = async (editedEvent, store) => {
  // 次の条件の調整対象のイベントを取得
  // 予定ステータス
  // 同じクルー
  // 更新されたイベント以外
  const targetEvents = _getTargetEvents(store, editedEvent, false);

  if (targetEvents.length === 0) {
    return;
  }

  try {
    // スケジュールステータスが予定=>計画に変更された場合の処理
    if (editedEvent.isFieldModified("scheduling_status")) {
      await _scheduledStatusChangedAsync(editedEvent, store);
    }
    await _removeAsync(editedEvent, store);

    await _ajastAsync(editedEvent, store);
    await _removeAsync(editedEvent, store);

    await _reassignManningVesselCrewId(editedEvent, store);
    await _removeAsync(editedEvent, store);

    await _concatAsync(editedEvent, store);
    await _removeAsync(editedEvent, store);

    // イベントのコピーと削除を適用
    await EventHelperCommon.applyReplaceAndDeleteAsync(store);
  } catch (e) {
    console.error("Error in handleVesselEventChangedAsync:", e);
  }
};

/**
 * 削除対象のイベントに削除フラグを立てる
 * @param {*} editedEvent
 * @param {*} store
 */
const _removeAsync = async (editedEvent, store) => {
  const targetEvents = _getTargetEvents(store, editedEvent, true);

  // 重なっているイベントを取得
  const overlappingEvents = targetEvents.filter(event => {
    return EventHelperCommon.isOverlapping(editedEvent, event);
  });

  for (const overlap of overlappingEvents) {
    // 更新されたイベント自体が包含されている場合は削除して終了
    if (EventHelperCommon.isContained(editedEvent, overlap)) {
      EventHelperCommon.softDelete(editedEvent);
      continue;
    }

    // 更新されたイベントに包含されている場合は削除
    if (EventHelperCommon.isContained(overlap, editedEvent)) {
      EventHelperCommon.softDelete(overlap);
      continue;
    }
  }
};

/**
 * 同じ船、同じ役職、同じクルーで隣あっているイベントを結合する
 * @param {*} editedEvent
 * @param {*} store
 * @returns
 */
const _concatAsync = async (editedEvent, store) => {
  const targetEvents = _getTargetEvents(store, editedEvent, true);

  // 隣接している同じリソースのイベントを取得
  const adjacentSameVesselEvents = targetEvents.filter(event => {
    return (
      EventHelperCommon.isAdjacent(editedEvent, event) &&
      editedEvent.resourceId === event.resourceId
    );
  });

  if (adjacentSameVesselEvents.length === 0) {
    return;
  }

  for (const adjacentEvent of adjacentSameVesselEvents) {
    // 隣接イベントが前にある場合
    if (adjacentEvent.startDate == editedEvent.endDate) {
      await editedEvent.setEndDate(adjacentEvent.endDate, false);
      EventHelperCommon.softDelete(adjacentEvent);
      continue;
    }

    // 隣接イベントが後にある場合
    if (adjacentEvent.endDate == editedEvent.startDate) {
      await adjacentEvent.setEndDate(editedEvent.endDate, false);
      EventHelperCommon.softDelete(editedEvent);
      continue;
    }
  }
};

/**
 * 乗船期間IDを振り直す
 * @param {*} イベント
 * @param {*} editedEvent
 * @param {*} store
 */
const _reassignManningVesselCrewId = async (editedEvent, store) => {
  const targetEvents = _getTargetEvents(store, editedEvent, true);

  // 同じ船舶のイベントを取得
  const sameVesselEvents = targetEvents.filter(event => {
    return EventHelperVessel.isSameVessel(editedEvent, event);
  });

  if (sameVesselEvents.length === 0) {
    return;
  }
  sameVesselEvents.push(editedEvent);
  // 開始日でソート
  sameVesselEvents.sort(EventHelperCommon.sortByStartDate);
  for (let i = 1; i < sameVesselEvents.length; i++) {
    const currentEvent = sameVesselEvents[i];
    const prevousEvent = sameVesselEvents[i - 1];

    // 繋がりのある期間内で乗船期間IDが異なる場合は、振り直して終了
    if (
      prevousEvent.endDate == currentEvent.startDate &&
      prevousEvent.enterprise_manning_vessel_crew_id !==
        currentEvent.enterprise_manning_vessel_crew_id
    ) {
      // 異なっていた乗船期間IDのイベントを取得
      // 前のイベントの乗船期間IDに合わせて振り直す
      const targetReplaceEvents = sameVesselEvents.filter(
        event =>
          event.enterprise_manning_vessel_crew_id ===
          currentEvent.enterprise_manning_vessel_crew_id
      );

      for (const targetEvent of targetReplaceEvents) {
        EventHelperCommon.changeEnterpriseManningVesselCrewId(
          targetEvent,
          prevousEvent.enterprise_manning_vessel_crew_id
        );
      }

      return;
    }
  }
};

/**
 * イベントの削除、調整を行う
 * @param {*} editedEvent
 * @param {*} store
 */
const _ajastAsync = async (editedEvent, store) => {
  const targetEvents = _getTargetEvents(store, editedEvent, true);
  if (targetEvents.length === 0) {
    return;
  }

  await _ajastPushAsync(editedEvent, targetEvents);

  await _ajastPullAsync(editedEvent, targetEvents);
};

const _ajastPushAsync = async (editedEvent, targetEvents) => {
  // ---- バーを延ばした時の調整 ----
  // 重なっているイベントを取得
  const overlappingEvents = targetEvents.filter(event => {
    return EventHelperCommon.isOverlapping(editedEvent, event);
  });

  for (const overlap of overlappingEvents) {
    // 包含されている場合は終了
    if (
      EventHelperCommon.isContained(editedEvent, overlap) ||
      EventHelperCommon.isContained(overlap, editedEvent)
    ) {
      continue;
    }
    // 左側が重なっている場合は終了日を更新
    if (EventHelperCommon.isOverlappingLeft(editedEvent, overlap)) {
      await overlap.setEndDate(editedEvent.startDate);
    }

    // 右側が重なっている場合は開始日を更新
    if (EventHelperCommon.isOverlappingRight(editedEvent, overlap)) {
      await overlap.setStartDate(editedEvent.endDate, false);
    }
  }
};

const _ajastPullAsync = async (editedEvent, targetEvents) => {
  // ---- バーを縮めた時の調整 ----
  // 同じ乗船期間IDのイベントを取得
  let samePeriodEvents = targetEvents.filter(event => {
    return (
      event.enterprise_manning_vessel_crew_id ===
      editedEvent.enterprise_manning_vessel_crew_id
    );
  });
  if (samePeriodEvents.length === 0) {
    return;
  }

  // 包含チェック
  // 更新イベントが包含されている場合は終了
  for (const event of samePeriodEvents) {
    if (EventHelperCommon.isContained(editedEvent, event)) {
      return;
    }
  }

  samePeriodEvents.push(editedEvent);
  // 開始日でソート
  samePeriodEvents.sort(EventHelperCommon.sortByStartDate);
  const editedEventIndex = samePeriodEvents.findIndex(
    event => event.id === editedEvent.id
  );

  if (editedEventIndex !== 0) {
    const prevEvent = samePeriodEvents[editedEventIndex - 1];
    if (prevEvent.endDate !== editedEvent.startDate) {
      await prevEvent.setEndDate(editedEvent.startDate);
    }
  }

  if (editedEventIndex !== samePeriodEvents.length - 1) {
    const nextEvent = samePeriodEvents[editedEventIndex + 1];
    if (nextEvent.startDate !== editedEvent.endDate) {
      await nextEvent.setStartDate(editedEvent.endDate, false);
    }
  }
};

// スケジュールステータスがPLANNEDからSCHEDULEDに変更された場合の処理
const _scheduledStatusChangedAsync = async (editedEvent, store) => {
  const targetEvents = _getTargetEvents(store, editedEvent, true);

  // 次のイベント検索
  // 重なっている、または隣接している
  const overlappingOrAdjacentEvents = targetEvents.filter(event => {
    return EventHelperCommon.isOverlappingOrAdjacent(editedEvent, event);
  });
  if (overlappingOrAdjacentEvents.length === 0) {
    return;
  }
  // 開始日ソート
  overlappingOrAdjacentEvents.sort(EventHelperCommon.sortByStartDate);

  // 同じリソースのイベントを取得
  const sameResouceEvents = overlappingOrAdjacentEvents.filter(event => {
    return editedEvent.resourceId === event.resourceId;
  });

  if (sameResouceEvents.length > 0) {
    const sameVesselEvent = sameResouceEvents[0];
    // 乗船期間IDを更新
    EventHelperCommon.setEnterpriseManningVesselCrewId(
      editedEvent,
      sameVesselEvent.enterprise_manning_vessel_crew_id
    );

    if (editedEvent.startDate < sameVesselEvent.startDate) {
      // 先頭のイベントの開始日よりも前にある場合は、先頭のイベントの終了日に合わせる
      await sameVesselEvent.setStartDate(editedEvent.startDate, false);
    }
    if (editedEvent.endDate > sameVesselEvent.endDate) {
      // 先頭のイベントの終了日よりも後にある場合は、先頭のイベントの終了日に合わせる
      await sameVesselEvent.setEndDate(editedEvent.endDate, false);
    }

    await editedEvent.setStartDate(sameVesselEvent.startDate, false);
    await editedEvent.setEndDate(sameVesselEvent.endDate, false);

    return;
  }

  // 先頭のイベントを取得
  const headEvent = overlappingOrAdjacentEvents[0];

  //船舶が同じ場合
  if (EventHelperVessel.isSameVessel(editedEvent, headEvent)) {
    EventHelperCommon.changeEnterpriseManningVesselCrewId(
      editedEvent,
      headEvent.enterprise_manning_vessel_crew_id
    );

    return;
  }

  // 別の船舶のイベント
  // 更新イベントが重なっている場合
  if (EventHelperCommon.isOverlapping(editedEvent, headEvent)) {
    // 重なったイベントと同じ乗船期間IDを持つイベントを取得
    const samePeriodEvents = targetEvents.filter(event => {
      return (
        event.enterprise_manning_vessel_crew_id ===
        headEvent.enterprise_manning_vessel_crew_id
      );
    });

    // 重なったイベントが先頭で開始日が更新イベントよりも後ろにある場合は何もしない
    if (
      samePeriodEvents[0].id == headEvent.id &&
      samePeriodEvents[0].startDate >= editedEvent.startDate
    ) {
      return;
    }

    // 重なったイベントが最後で終了日が更新イベントよりも前の場合は何もしない
    if (
      samePeriodEvents[samePeriodEvents.length - 1].id == headEvent.id &&
      samePeriodEvents[samePeriodEvents.length - 1].endDate <=
        editedEvent.endDate
    ) {
      return;
    }

    // 重なったイベント以降のイベントを取得
    const targetsCopyEvents = samePeriodEvents.filter(
      event => headEvent.startDate < event.startDate
    );

    const phantom_group_key = `gk_${window.crypto.randomUUID()}`;

    // 更新イベントが重なったイベントの内部に完全に収まっている場合
    // 重なったイベントを分割
    if (
      headEvent.startDate < editedEvent.startDate &&
      headEvent.endDate > editedEvent.endDate
    ) {
      const copyEvent = await EventHelperCommon.copyEventAsync(
        headEvent,
        store
      );
      EventHelperCommon.setEnterpriseManningVesselCrewId(copyEvent, null);
      EventHelperCommon.setPhantomGroupKey(copyEvent, phantom_group_key);
      await copyEvent.setStartDate(editedEvent.endDate, false);
      await headEvent.setEndDate(editedEvent.startDate);
    }

    // 重なり以降のイベントの乗船期間IDを振り直す
    targetsCopyEvents.forEach(event => {
      EventHelperCommon.setPhantomGroupKey(event, phantom_group_key);
      EventHelperCommon.changeEnterpriseManningVesselCrewId(event, null);
    });

    return;
  }
};

/**
 *
 * @param {*} store イベントストア
 * @param {*} editedEvent 変更されたイベント
 * @param {*} isActiveOnly 削除フラグが立っていないイベントのみを対象とするか
 * @returns
 */
const _getTargetEvents = (store, editedEvent, isActiveOnly) => {
  return store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.crew_id === editedEvent.crew_id &&
      event.id !== editedEvent.id &&
      (!isActiveOnly || !EventHelperCommon.isSoftDeleted(event))
    );
  }, true);
};
